import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  TrendingUp,
  TrendingDown,
  Sparkles,
  Calendar,
  BarChart3,
  Loader2,
  ArrowUpRight,
  IndianRupee,
  Users,
  Wallet,
  Image,
  Package,
} from "lucide-react";
import { device } from "../../atoms/Utlis/media";
import BrandDatePicker from "../../atoms/Utlis/BrandDatePicker";
import {
  getUserAnalyticsAPI,
  type UserAnalyticsParams,
  type AnalyticsPeriod,
} from "../../../Redux/ApiCalls/Dashboard/AnalyticsAPI";
import type { UserAnalyticsResponse } from "../../../Data/analytics.dto";

// Types
interface MetricItem {
  value: number;
  change_percentage: number;
  period: string;
  [key: string]: unknown;
}

interface UpcomingEvent {
  date: string;
  day_label: string;
  title: string;
  customer_name: string;
  event_type: string;
  order_no: string;
  event_time: string;
}

// Colors
const PURPLE = "#D776A4";
const ORANGE = "#F2AD6D";
const GRADIENT = "linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%)";
const FILL_OPACITY = 0.75;
const GID = "cg";

const CARD_META: Record<string, { icon: React.ElementType; tone: string }> = {
  revenue_this_month: { icon: IndianRupee, tone: "#c94daa" },
  outstanding_payments: { icon: Wallet, tone: "#e44862" },
  new_leads_this_week: { icon: Users, tone: "#35b7c8" },
  lead_conversion_rate: { icon: TrendingUp, tone: "#31a66a" },
  upcoming_events_this_week: { icon: Calendar, tone: "#f09a3e" },
  pipeline_snapshot: { icon: BarChart3, tone: "#7c4dff" },
  total_albums: { icon: Image, tone: "#7c4dff" },
  total_folders: { icon: Package, tone: "#35b7c8" },
  total_images: { icon: Image, tone: "#c94daa" },
  total_booked_packages: { icon: Package, tone: "#f09a3e" },
  total_payments: { icon: Wallet, tone: "#31a66a" },
  total_members: { icon: Users, tone: "#35b7c8" },
  total_event_inquiries: { icon: Calendar, tone: "#f09a3e" },
};

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Spinner = styled(Loader2)`
  animation: ${spin} 0.8s linear infinite;
`;

// Summary card definitions
// FIX 1: Added "pipeline_snapshot" which was missing from CARDS
const CARDS = [
  { key: "revenue_this_month", label: "Revenue This Month", route: "/dashboard/payments", format: "currency" },
  { key: "outstanding_payments", label: "Outstanding Payments", route: "/dashboard/payments", format: "currency" },
  { key: "new_leads_this_week", label: "New Leads This Week", route: "/dashboard/events/userCreated", format: "number" },
  { key: "lead_conversion_rate", label: "Lead Conversion Rate", route: "/dashboard/events/userCreated", format: "percent" },
  { key: "upcoming_events_this_week", label: "Upcoming Events", route: "/dashboard/events/userCreated", format: "number" },
  { key: "pipeline_snapshot", label: "Pipeline Snapshot", route: "/dashboard/events/userCreated", format: "number" }, // ← ADDED
  { key: "total_albums", label: "Total Albums", route: "/dashboard/albums/all", format: "number" },
  { key: "total_folders", label: "Total Folders", route: "/dashboard/albums/all", format: "number" },
  { key: "total_images", label: "Total Images", route: "/dashboard/albums/all", format: "number" },
  { key: "total_booked_packages", label: "Booked Packages", route: "/dashboard/user/transactions", format: "number" },
  { key: "total_payments", label: "Total Payments", route: "/dashboard/payments", format: "currency" },
  { key: "total_members", label: "Total Members", route: "/dashboard/members/all", format: "number" },
  { key: "total_event_inquiries", label: "Event Inquiries", route: "/dashboard/events/userCreated", format: "number" },
] as const;

function fmtValue(value: number, format: string): string {
  if (format === "currency")
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(value);
  if (format === "percent") return `${value.toFixed(1)}%`;
  return new Intl.NumberFormat("en-IN").format(value);
}

// Helpers
function fmtMonth(s: string): string {
  try {
    const [y, m] = s.split("-").map(Number);
    return new Date(y, (m ?? 1) - 1).toLocaleDateString("en-US", { month: "short", year: "2-digit" });
  } catch { return s; }
}

function buildYearData(year: number, data: { month: string; count: number }[]) {
  const map = new Map(data.map((d) => [d.month, d.count]));
  return Array.from({ length: 12 }, (_, i) => {
    const key = `${year}-${String(i + 1).padStart(2, "0")}`;
    return { monthLabel: fmtMonth(key), count: map.get(key) ?? 0 };
  });
}

const ELLIPSIS = "...";
function wrapLabel(text: string, maxLineLength = 8, maxLines = 2): string[] {
  const s = String(text ?? "").trim();
  if (!s) return [];
  const lines: string[] = [];
  let remaining = s;

  while (remaining && lines.length < maxLines) {
    if (remaining.length <= maxLineLength) {
      lines.push(remaining);
      remaining = "";
      break;
    }

    let slice = remaining.slice(0, maxLineLength);
    const nextChar = remaining[maxLineLength];
    const breakAtSpace = slice.lastIndexOf(" ");

    if (nextChar !== " " && breakAtSpace > 2) {
      slice = slice.slice(0, breakAtSpace);
    }

    slice = slice.trim();
    if (!slice) {
      slice = remaining.slice(0, maxLineLength).trim();
    }

    lines.push(slice);
    remaining = remaining.slice(slice.length).trim();
  }

  if (remaining && lines.length > 0) {
    const last = lines[lines.length - 1];
    lines[lines.length - 1] =
      last.slice(0, Math.max(1, maxLineLength - ELLIPSIS.length)).trimEnd() + ELLIPSIS;
  }

  return lines;
}

function payloadVal(p: unknown): string {
  if (p == null) return "";
  if (typeof p === "string" || typeof p === "number") return String(p);
  if (typeof p === "object" && "value" in p) return String((p as { value: unknown }).value ?? "");
  return "";
}

function AlbumTick(props: { x?: number; y?: number; payload?: unknown; fontSize?: number; fill?: string }) {
  const { x = 0, y = 0, payload, fontSize = 11, fill = "#6b7280" } = props;
  const val = payloadVal(payload);
  const lines = wrapLabel(val);
  const lineHeight = fontSize + 2;
  return (
    <g transform={`translate(${x},${y})`}>
      {val && <title>{val}</title>}
      {lines.map((line, index) => (
        <text
          key={`${line}-${index}`}
          x={0}
          y={0}
          dy={fontSize + index * lineHeight}
          textAnchor="middle"
          fill={fill}
          fontSize={fontSize}
          style={{ fontFamily: "Urbanist,sans-serif" }}
        >
          {line}
        </text>
      ))}
    </g>
  );
}

function Grad({ id }: { id: string }) {
  return (
    <defs>
      <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={ORANGE} stopOpacity={FILL_OPACITY} />
        <stop offset="100%" stopColor={PURPLE} stopOpacity={FILL_OPACITY} />
      </linearGradient>
    </defs>
  );
}

const TT = { borderRadius: 12, border: "none", boxShadow: "0 4px 20px rgba(0,0,0,0.1)" };
const AX = { axisLine: { stroke: "#e5e7eb" }, tickLine: false as const };

function getEndMinMax(start: string) {
  if (!start) return null;
  const y = new Date(start).getFullYear();
  if (isNaN(y)) return null;
  return { min: `${y}-01-01`, max: `${y}-12-31` };
}

function normalizeMetric(raw: unknown): MetricItem | null {
  if (raw == null) return null;
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return { value: raw, change_percentage: 0, period: "" };
  }
  if (typeof raw === "string" && raw.trim() !== "" && !Number.isNaN(Number(raw))) {
    return { value: Number(raw), change_percentage: 0, period: "" };
  }
  if (typeof raw !== "object") return null;
  const obj = raw as Record<string, unknown>;
  const valueCandidate =
    obj.value ?? obj.total_amount ?? obj.count ?? obj.total ?? obj.current;
  const value =
    typeof valueCandidate === "number"
      ? valueCandidate
      : typeof valueCandidate === "string" && !Number.isNaN(Number(valueCandidate))
        ? Number(valueCandidate)
        : null;
  if (value == null || !Number.isFinite(value)) return null;
  const changeRaw = obj.change_percentage;
  const change =
    typeof changeRaw === "number" && Number.isFinite(changeRaw)
      ? changeRaw
      : typeof changeRaw === "string" && !Number.isNaN(Number(changeRaw))
        ? Number(changeRaw)
        : 0;
  const period = typeof obj.period === "string" ? obj.period : "";
  return { value, change_percentage: change, period };
}

// Component
const AnalyticsPage: React.FC = () => {
  const [period, setPeriod] = useState<AnalyticsPeriod>("this_year");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [data, setData] = useState<UserAnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dateError, setDateError] = useState<string | null>(null);

  const endMM = getEndMinMax(startDate);

  const fetch_ = useCallback(async () => {
    setLoading(true); setError(null);
    const params: UserAnalyticsParams = { period };
    if (period === "custom") { params.start_date = startDate; params.end_date = endDate; }
    try { setData(await getUserAnalyticsAPI(params)); }
    catch { setError("Failed to load analytics."); }
    finally { setLoading(false); }
  }, [period, startDate, endDate]);

  useEffect(() => {
    if (period !== "custom") { fetch_(); return; }
    if (!startDate || !endDate) { setData(null); return; }
    if (new Date(startDate).getFullYear() !== new Date(endDate).getFullYear()) { setData(null); return; }
    fetch_();
  }, [period, startDate, endDate, fetch_]);

  const handleApply = () => {
    setDateError(null);
    if (!startDate || !endDate) { setDateError("Select both dates."); return; }
    if (new Date(startDate).getFullYear() !== new Date(endDate).getFullYear()) { setDateError("Dates must be in same year."); return; }
    if (endDate < startDate) { setDateError("End must be after start."); return; }
    fetch_();
  };

  const payload = data?.data;
  const sm = payload?.summary_metrics as Record<string, MetricItem> | undefined;
  const charts = payload?.charts as Record<string, unknown> | undefined;

  const chartYear = period === "this_year" ? new Date().getFullYear()
    : period === "last_year" ? new Date().getFullYear() - 1
      : startDate ? new Date(startDate).getFullYear() : new Date().getFullYear();

  // helpers to safely read chart data
  type BarData = { month?: string; count?: number;[k: string]: unknown };
  const getChart = (key: string) => charts?.[key] as { title: string; data: BarData[]; y_axis?: string; note?: string; labels?: string[] } | undefined;
  const getChartData = (key: string): BarData[] => getChart(key)?.data ?? [];
  const getDonut = () => charts?.album_access_type_distribution as {
    title: string; labels: string[];
    data: { guest_code_usage_count: number; full_access_code_usage_count: number; total: number }
  } | undefined;
  const getRevComp = () => charts?.revenue_this_month_comparison as { title: string; data: { this_month: number; last_month: number } } | undefined;
  const getConvTrend = () => charts?.lead_conversion_trend as { title: string; data: { current: number; previous: number; inquiries_last_30_days: number; booked_last_30_days: number } } | undefined;
  const getEvents = () => charts?.upcoming_events_this_week as { title: string; data: UpcomingEvent[] } | undefined;
  const getPipeline = () => charts?.pipeline_snapshot as { title: string; data: { label: string; count: number }[] } | undefined;
  const getLeads = () => charts?.new_leads_by_source as { title: string; data: { label: string; count: number }[] } | undefined;

  // FIX 2: Helper to read the outstanding_payments summary chart
  const getOutstandingChart = () => charts?.outstanding_payments as {
    title: string;
    type: string;
    data: { total_amount: number; bookings_count: number; currency: string };
  } | undefined;

  const visibleCards = useMemo(() => {
    if (!sm) return [];
    const knownKeys = new Set<string>(CARDS.map((c) => c.key));
    const fromCards = CARDS.map((card) => {
      const item = normalizeMetric(sm[card.key]);
      if (!item) return null;
      return { ...card, item };
    }).filter(Boolean) as Array<{
      key: string;
      label: string;
      route: string;
      format: string;
      item: MetricItem;
    }>;

    // Include any extra summary_metrics keys the API returns that aren't in CARDS
    const extras = Object.keys(sm)
      .filter((key) => !knownKeys.has(key))
      .map((key) => {
        const item = normalizeMetric(sm[key]);
        if (!item) return null;
        return {
          key,
          label: key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
          route: "/dashboard/",
          format: "number" as const,
          item,
        };
      })
      .filter(Boolean) as Array<{
        key: string;
        label: string;
        route: string;
        format: string;
        item: MetricItem;
      }>;

    return [...fromCards, ...extras];
  }, [sm]);

  const renderStatCard = (
    key: string,
    label: string,
    route: string,
    format: string,
    item: MetricItem
  ) => {
    const change = Number(item.change_percentage) || 0;
    const pos = change >= 0;
    const meta = CARD_META[key] ?? { icon: BarChart3, tone: "#c94daa" };
    const Icon = meta.icon;
    return (
      <StatCardLink key={key} to={route}>
        <StatCard>
          <StatCardTop>
            <StatIcon $tone={meta.tone}>
              <Icon size={18} />
            </StatIcon>
            <StatArrow aria-hidden>
              <ArrowUpRight size={14} />
            </StatArrow>
          </StatCardTop>
          <StatLabel>{label}</StatLabel>
          <StatValue>{fmtValue(item.value, format)}</StatValue>
          <StatFooter>
            <StatChange $positive={pos}>
              {pos ? <TrendingUp size={12} strokeWidth={2.5} /> : <TrendingDown size={12} strokeWidth={2.5} />}
              {pos ? "+" : ""}
              {change.toFixed(1)}%
            </StatChange>
            {item.period ? <StatPeriod>{item.period}</StatPeriod> : null}
          </StatFooter>
        </StatCard>
      </StatCardLink>
    );
  };

  return (
    <Page>
      <Hero>
        <HeroGlow />
        <HeroContent>
          <HeroText>
            <HeroBadge>
              <Sparkles size={11} />
              Studio insights
            </HeroBadge>
            <PageTitle className="linear-text">Dashboard</PageTitle>
            <PageSubtitle>
              Track your studio&apos;s performance — leads, bookings, albums, and payments.
            </PageSubtitle>
          </HeroText>
          <MaintenanceChip>
            <Calendar size={13} />
            Server maintenance: 2:00 AM – 7:00 AM
          </MaintenanceChip>
        </HeroContent>
      </Hero>

      <FilterCard>
        <FilterLeft>
          <Calendar size={14} />
          Period
        </FilterLeft>
        <FilterControls>
          <PillBtn $active={period === "this_year"} onClick={() => setPeriod("this_year")}>
            This year
          </PillBtn>
          <PillBtn $active={period === "last_year"} onClick={() => setPeriod("last_year")}>
            Last year
          </PillBtn>
          <PillBtn $active={period === "custom"} onClick={() => setPeriod("custom")}>
            Custom
          </PillBtn>
          {period === "custom" && (
            <>
              <FilterDateWrap>
                <BrandDatePicker
                  value={startDate}
                  onChange={(v) => {
                    setStartDate(v);
                    setDateError(null);
                  }}
                  size="compact"
                />
              </FilterDateWrap>
              <FilterDateWrap>
                <BrandDatePicker
                  value={endDate}
                  onChange={(v) => {
                    setEndDate(v);
                    setDateError(null);
                  }}
                  minDate={endMM?.min}
                  maxDate={endMM?.max}
                  disabled={!startDate}
                  size="compact"
                />
              </FilterDateWrap>
              <ApplyBtn type="button" onClick={handleApply}>
                Apply
              </ApplyBtn>
              {dateError && <ErrSpan>{dateError}</ErrSpan>}
            </>
          )}
        </FilterControls>
      </FilterCard>

      <ContentWrap>
        {loading && (
          <LoadingWrap>
            <Spinner size={28} color="#c94daa" />
            Loading analytics…
          </LoadingWrap>
        )}

        {!loading && sm && visibleCards.length > 0 && (
          <MetricsPanel>
            <PanelHeader>
              <HeaderLeft>
                <PanelTitle>Overview</PanelTitle>
                <PanelHint>
                  {visibleCards.length} metrics
                </PanelHint>
              </HeaderLeft>
            </PanelHeader>

            <StatsGrid>
              {visibleCards.map(({ key, label, route, format, item }) =>
                renderStatCard(key, label, route, format, item)
              )}
            </StatsGrid>
          </MetricsPanel>
        )}

        {error && <ErrorBanner>{error}</ErrorBanner>}

        {!loading && charts && (
          <>
            <ChartsSectionHeader>
              <BarChart3 size={16} color="#c94daa" />
              <span>Charts & insights</span>
            </ChartsSectionHeader>
            <ChartsGrid>

              {/* 1. New Leads by Source */}
              {(getLeads()?.data?.length ?? 0) > 0 && (
                <ChartCard>
                  <ChartHdr><ChartTitle>{getLeads()!.title}</ChartTitle></ChartHdr>
                  <CW>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={getLeads()!.data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <Grad id={`${GID}-leads`} />
                        <XAxis dataKey="label" {...AX} tick={{ fontSize: 12, fill: "#6b7280" }} />
                        <YAxis {...AX} tick={{ fontSize: 12, fill: "#6b7280" }} />
                        <Tooltip contentStyle={TT} />
                        <Bar dataKey="count" radius={[4, 4, 0, 0]} fill={`url(#${GID}-leads)`} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CW>
                </ChartCard>
              )}

              {/* 2. Pipeline Snapshot */}
              {(getPipeline()?.data?.length ?? 0) > 0 && (
                <ChartCard>
                  <ChartHdr><ChartTitle>{getPipeline()!.title}</ChartTitle></ChartHdr>
                  <CW>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={getPipeline()!.data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <Grad id={`${GID}-pipe`} />
                        <XAxis dataKey="label" {...AX} tick={{ fontSize: 11, fill: "#6b7280" }} />
                        <YAxis {...AX} tick={{ fontSize: 12, fill: "#6b7280" }} />
                        <Tooltip contentStyle={TT} />
                        <Bar dataKey="count" radius={[4, 4, 0, 0]} fill={`url(#${GID}-pipe)`} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CW>
                </ChartCard>
              )}

              {/* 3. Revenue Comparison */}
              {getRevComp()?.data && (
                <ChartCard>
                  <ChartHdr><ChartTitle>{getRevComp()!.title}</ChartTitle></ChartHdr>
                  <CW>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={[
                          { label: "Last Month", value: getRevComp()!.data.last_month },
                          { label: "This Month", value: getRevComp()!.data.this_month },
                        ]}
                        margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                      >
                        <Grad id={`${GID}-rev`} />
                        <XAxis dataKey="label" {...AX} tick={{ fontSize: 12, fill: "#6b7280" }} />
                        <YAxis {...AX} tick={{ fontSize: 12, fill: "#6b7280" }}
                          tickFormatter={(v) => `₹${(Number(v) / 1000).toFixed(0)}k`} />
                        <Tooltip formatter={(v) => [`₹${Number(v).toLocaleString("en-IN")}`, "Revenue"]} contentStyle={TT} />
                        <Bar dataKey="value" radius={[4, 4, 0, 0]} fill={`url(#${GID}-rev)`} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CW>
                </ChartCard>
              )}

              {/* 4. Lead Conversion Trend */}
              {getConvTrend()?.data && (
                <ChartCard>
                  <ChartHdr><ChartTitle>{getConvTrend()!.title}</ChartTitle></ChartHdr>
                  <CW>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={[
                          { label: "Previous", value: getConvTrend()!.data.previous },
                          { label: "Current", value: getConvTrend()!.data.current },
                        ]}
                        margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                      >
                        <Grad id={`${GID}-conv`} />
                        <XAxis dataKey="label" {...AX} tick={{ fontSize: 12, fill: "#6b7280" }} />
                        <YAxis {...AX} tick={{ fontSize: 12, fill: "#6b7280" }} tickFormatter={(v) => `${Number(v)}%`} />
                        <Tooltip formatter={(v) => [`${Number(v).toFixed(1)}%`, "Rate"]} contentStyle={TT} />
                        <Bar dataKey="value" radius={[4, 4, 0, 0]} fill={`url(#${GID}-conv)`} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CW>
                  <ChartNote>
                    {getConvTrend()!.data.inquiries_last_30_days} inquiries → {getConvTrend()!.data.booked_last_30_days} booked (last 30 days)
                  </ChartNote>
                </ChartCard>
              )}

              {/* 5. Upcoming Events */}
              {(getEvents()?.data?.length ?? 0) > 0 && (
                <ChartCard>
                  <ChartHdr><ChartTitle>{getEvents()!.title}</ChartTitle></ChartHdr>
                  <EventsList>
                    {getEvents()!.data.map((ev, i) => (
                      <EventRow key={i}>
                        <EventDateBox>
                          <EventDay>{ev.day_label}</EventDay>
                          <EventNum>{ev.date.split("-")[2]}</EventNum>
                        </EventDateBox>
                        <EventBody>
                          <EventName>{ev.title}</EventName>
                          <EventMeta>{ev.order_no} · {ev.event_time}</EventMeta>
                        </EventBody>
                        <EventBadge>{ev.event_type}</EventBadge>
                      </EventRow>
                    ))}
                  </EventsList>
                </ChartCard>
              )}

              {/* FIX 2: Outstanding Payments Summary Chart */}
              {/* This chart has type "summary" with no rows of data, so we render it
                as a dedicated info card showing total_amount and bookings_count. */}
              {getOutstandingChart()?.data && (
                <ChartCard>
                  <ChartHdr><ChartTitle>{getOutstandingChart()!.title}</ChartTitle></ChartHdr>
                  <OutstandingGrid>
                    <OutstandingItem>
                      <OutstandingLabel>Total Outstanding</OutstandingLabel>
                      <OutstandingValue>
                        {new Intl.NumberFormat("en-IN", {
                          style: "currency",
                          currency: getOutstandingChart()!.data.currency ?? "INR",
                          maximumFractionDigits: 0,
                        }).format(getOutstandingChart()!.data.total_amount)}
                      </OutstandingValue>
                    </OutstandingItem>
                    <OutstandingDivider />
                    <OutstandingItem>
                      <OutstandingLabel>Active Bookings</OutstandingLabel>
                      <OutstandingValue>{getOutstandingChart()!.data.bookings_count}</OutstandingValue>
                    </OutstandingItem>
                  </OutstandingGrid>
                  <OutstandingNote>
                    Payments pending across {getOutstandingChart()!.data.bookings_count} active booking
                    {getOutstandingChart()!.data.bookings_count !== 1 ? "s" : ""}
                  </OutstandingNote>
                </ChartCard>
              )}

              {/* 6. Images Uploaded Over Time */}
              {(getChartData("images_uploaded_over_time").length) > 0 && (
                <ChartCard>
                  <ChartHdr><ChartTitle>{getChart("images_uploaded_over_time")!.title}</ChartTitle></ChartHdr>
                  <CW>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={buildYearData(chartYear, getChartData("images_uploaded_over_time") as { month: string; count: number }[])}
                        margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <Grad id={`${GID}-img`} />
                        <XAxis dataKey="monthLabel" {...AX} tick={{ fontSize: 11, fill: "#6b7280" }} />
                        <YAxis {...AX} tick={{ fontSize: 12, fill: "#6b7280" }} />
                        <Tooltip formatter={(v) => [Number(v), "Images"]} contentStyle={TT} />
                        <Bar dataKey="count" radius={[4, 4, 0, 0]} fill={`url(#${GID}-img)`} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CW>
                </ChartCard>
              )}

              {/* 7. Albums Growth Over Time */}
              {(getChartData("albums_growth_over_time").length) > 0 && (
                <ChartCard>
                  <ChartHdr><ChartTitle>{getChart("albums_growth_over_time")!.title}</ChartTitle></ChartHdr>
                  <CW>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={buildYearData(chartYear, getChartData("albums_growth_over_time") as { month: string; count: number }[])}
                        margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <Grad id={`${GID}-alb`} />
                        <XAxis dataKey="monthLabel" {...AX} tick={{ fontSize: 11, fill: "#6b7280" }} />
                        <YAxis {...AX} tick={{ fontSize: 12, fill: "#6b7280" }} />
                        <Tooltip formatter={(v) => [Number(v), "Albums"]} contentStyle={TT} />
                        <Area type="monotone" dataKey="count" stroke={PURPLE} strokeWidth={2} fill={`url(#${GID}-alb)`} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </CW>
                </ChartCard>
              )}

              {/* 8. Folder Distribution */}
              {(getChartData("folder_distribution_per_album").length) > 0 && (
                <ChartCard>
                  <ChartHdr><ChartTitle>{getChart("folder_distribution_per_album")!.title}</ChartTitle></ChartHdr>
                  <CW>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={getChartData("folder_distribution_per_album")} margin={{ top: 10, right: 10, left: 0, bottom: 48 }}>
                        <Grad id={`${GID}-fold`} />
                        <XAxis dataKey="album_name" {...AX} tick={<AlbumTick fontSize={11} fill="#6b7280" />} height={64} interval={0} />
                        <YAxis {...AX} tick={{ fontSize: 12, fill: "#6b7280" }} />
                        <Tooltip contentStyle={TT} />
                        <Bar dataKey="folder_count" radius={[4, 4, 0, 0]} fill={`url(#${GID}-fold)`} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CW>
                </ChartCard>
              )}

              {/* 9. Album Access Type Donut */}
              {(getDonut()?.data?.total ?? 0) > 0 && (
                <ChartCard>
                  <ChartHdr><ChartTitle>{getDonut()!.title}</ChartTitle></ChartHdr>
                  <CW>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={[
                            { name: getDonut()!.labels[0] ?? "Guest", value: getDonut()!.data.guest_code_usage_count },
                            { name: getDonut()!.labels[1] ?? "Full Access", value: getDonut()!.data.full_access_code_usage_count },
                          ].filter((d) => d.value > 0)}
                          cx="50%" cy="45%" innerRadius={60} outerRadius={100} paddingAngle={2} dataKey="value"
                        >
                          <Cell fill={PURPLE} fillOpacity={FILL_OPACITY} stroke="none" />
                          <Cell fill={ORANGE} fillOpacity={FILL_OPACITY} stroke="none" />
                        </Pie>
                        <Tooltip formatter={(v) => [Number(v), ""]} contentStyle={TT} />
                      </PieChart>
                    </ResponsiveContainer>
                  </CW>
                  <PieLeg>
                    {getDonut()!.labels.map((lbl, i) => (
                      <LegItem key={i}><LegDot $c={i === 0 ? PURPLE : ORANGE} />{lbl}</LegItem>
                    ))}
                  </PieLeg>
                </ChartCard>
              )}

              {/* 10. Redeem Code Usage */}
              {(getChartData("redeem_code_usage_per_album").length) > 0 && (
                <ChartCard>
                  <ChartHdr><ChartTitle>{getChart("redeem_code_usage_per_album")!.title}</ChartTitle></ChartHdr>
                  <CW>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={getChartData("redeem_code_usage_per_album")} margin={{ top: 10, right: 10, left: 0, bottom: 48 }}>
                        <Grad id={`${GID}-rdm`} />
                        <XAxis dataKey="album_name" {...AX} tick={<AlbumTick fontSize={11} fill="#6b7280" />} height={64} interval={0} />
                        <YAxis {...AX} tick={{ fontSize: 12, fill: "#6b7280" }} />
                        <Tooltip contentStyle={TT} />
                        <Bar dataKey="usage_count" radius={[4, 4, 0, 0]} fill={`url(#${GID}-rdm)`} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CW>
                </ChartCard>
              )}

              {/* 11. Event Inquiries Trend */}
              {(getChartData("event_inquiries_trend").length) > 0 && (() => {
                const d = (getChartData("event_inquiries_trend") as { month: string; count: number }[])
                  .map((r) => ({ ...r, monthLabel: fmtMonth(r.month) }));
                return (
                  <ChartCard>
                    <ChartHdr><ChartTitle>{getChart("event_inquiries_trend")!.title}</ChartTitle></ChartHdr>
                    <CW>
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={d} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                          <Grad id={`${GID}-inq`} />
                          <XAxis dataKey="monthLabel" {...AX} tick={{ fontSize: 12, fill: "#6b7280" }} />
                          <YAxis {...AX} tick={{ fontSize: 12, fill: "#6b7280" }} />
                          <Tooltip formatter={(v) => [Number(v), "Inquiries"]} contentStyle={TT} />
                          <Area type="monotone" dataKey="count" stroke={PURPLE} strokeWidth={2}
                            fill={`url(#${GID}-inq)`}
                            dot={{ fill: PURPLE, r: 4, strokeWidth: 0 }}
                            activeDot={{ fill: PURPLE, r: 6, stroke: "#fff", strokeWidth: 2 }} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </CW>
                  </ChartCard>
                );
              })()}

              {/* 12. Album Popularity */}
              {(getChartData("album_popularity").length) > 0 && (
                <ChartCard>
                  <ChartHdr><ChartTitle>{getChart("album_popularity")!.title}</ChartTitle></ChartHdr>
                  {getChart("album_popularity")!.note && <ChartNote>{getChart("album_popularity")!.note}</ChartNote>}
                  <CW>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={getChartData("album_popularity")} margin={{ top: 10, right: 10, left: 0, bottom: 48 }}>
                        <Grad id={`${GID}-pop`} />
                        <XAxis dataKey="album_name" {...AX} tick={<AlbumTick fontSize={11} fill="#6b7280" />} height={64} interval={0} />
                        <YAxis {...AX} tick={{ fontSize: 12, fill: "#6b7280" }} />
                        <Tooltip contentStyle={TT} />
                        <Bar dataKey="total_shared_codes" name="Shared Codes" radius={[4, 4, 0, 0]} fill={`url(#${GID}-pop)`} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CW>
                </ChartCard>
              )}

            </ChartsGrid>
          </>
        )}

        {!loading && !error && payload && !sm && !charts && (
          <EmptyState>No analytics data for the selected period.</EmptyState>
        )}
      </ContentWrap>
    </Page>
  );
};

export default AnalyticsPage;

// Styled Components

const Page = styled.div`
  width: 100%;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex-shrink: 0;
  box-sizing: border-box;
  overflow: visible;
  font-family: Urbanist, sans-serif;
  background:
    radial-gradient(ellipse 70% 40% at 0% 0%, rgba(201, 77, 170, 0.05), transparent 55%),
    radial-gradient(ellipse 50% 35% at 100% 0%, rgba(255, 201, 81, 0.06), transparent 50%),
    transparent;

  @media ${device.tablet} {
    padding: 16px;
    gap: 14px;
  }
`;

const Hero = styled.div`
  position: relative;
  overflow: visible;
  flex-shrink: 0;
  border-radius: 20px;
  padding: 24px 26px;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.16) 0%,
    rgba(206, 86, 176, 0.12) 55%,
    rgba(53, 183, 200, 0.08) 100%
  );
  border: 1px solid rgba(201, 77, 170, 0.12);
  box-shadow: 0 8px 28px rgba(201, 77, 170, 0.08);
`;

const HeroGlow = styled.div`
  pointer-events: none;
  position: absolute;
  top: -80px;
  right: -4%;
  width: 240px;
  height: 240px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 201, 81, 0.32) 0%, transparent 70%);
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const HeroText = styled.div`
  display: flex;
  flex-direction: column;
`;

const HeroBadge = styled.span`
  display: inline-flex;
  width: fit-content;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
  padding: 5px 11px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(201, 77, 170, 0.14);
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #c94daa;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 800;
  line-height: 1.25;
  padding: 2px 0;
`;

const PageSubtitle = styled.p`
  margin: 0px 0 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.62);
  max-width: 560px;
`;

const MaintenanceChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 999px;
  background: ${GRADIENT};
  color: #fff;
  font-family: Urbanist, sans-serif;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
`;

const FilterCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 16px 20px;
  border-radius: 18px;
  background: #fff;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 8px 24px rgba(23, 23, 23, 0.04);
  flex-shrink: 0;
`;

const FilterLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(23, 23, 23, 0.55);
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const FilterControls = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const ContentWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex-shrink: 0;
`;

const LoadingWrap = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 12px;
  min-height: 320px;
  color: rgba(23, 23, 23, 0.55);
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  font-weight: 500;
`;

const MetricsPanel = styled.div`
  border-radius: 20px;
  background: #fff;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 8px 24px rgba(23, 23, 23, 0.04);
  overflow: hidden;
  flex-shrink: 0;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 14px 18px;
  border-bottom: 1px solid rgba(23, 23, 23, 0.06);
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
  min-width: 0;
`;

const PanelTitle = styled.h2`
  margin: 0;
  font-size: 15px;
  font-weight: 800;
  color: #171717;
`;

const PanelHint = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.45);
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  padding: 14px 18px 18px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media ${device.tablet} {
    grid-template-columns: repeat(2, 1fr);
  }

  @media ${device.mobileL} {
    grid-template-columns: 1fr;
  }
`;

const StatCardLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  display: flex;
  min-width: 0;
`;

const StatCard = styled.div`
  width: 100%;
  background: #fafafa;
  border-radius: 14px;
  padding: 14px 16px;
  border: 1px solid rgba(23, 23, 23, 0.06);
  display: flex;
  flex-direction: column;
  gap: 4px;
  cursor: pointer;
  transition: box-shadow 0.2s, transform 0.15s, border-color 0.15s, background 0.15s;

  &:hover {
    background: #fff;
    border-color: rgba(201, 77, 170, 0.18);
    box-shadow: 0 8px 24px rgba(201, 77, 170, 0.1);
    transform: translateY(-1px);
  }
`;

const StatCardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2px;
`;

const StatIcon = styled.div<{ $tone: string }>`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => `${p.$tone}14`};
  color: ${(p) => p.$tone};
`;

const StatArrow = styled.span`
  color: rgba(23, 23, 23, 0.25);
  transition: color 0.15s, transform 0.15s;

  ${StatCardLink}:hover & {
    color: #c94daa;
    transform: translate(1px, -1px);
  }
`;

const StatLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.45);
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const StatValue = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 22px;
  font-weight: 800;
  color: #171717;
  line-height: 1.1;
`;

const StatFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 2px;
`;

const StatChange = styled.div<{ $positive: boolean }>`
  display: flex;
  align-items: center;
  gap: 3px;
  font-family: Urbanist, sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: ${(p) => (p.$positive ? "#16a34a" : "#dc2626")};
`;

const StatPeriod = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  color: rgba(23, 23, 23, 0.4);
`;

const PillBtn = styled.button<{ $active?: boolean }>`
  padding: 8px 16px;
  border-radius: 12px;
  border: 1px solid ${(p) => (p.$active ? "transparent" : "rgba(23, 23, 23, 0.08)")};
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  background: ${(p) =>
    p.$active
      ? "linear-gradient(144.01deg, rgba(255,201,81,0.5) 0%, rgba(201,77,170,0.5) 100%)"
      : "#fafafa"};
  color: ${(p) => (p.$active ? "#111" : "#6b7280")};
  transition: all 0.18s;

  &:hover {
    opacity: 0.9;
  }
`;

const FilterDateWrap = styled.div`
  min-width: 160px;
`;

const ApplyBtn = styled.button`
  padding: 8px 16px;
  border-radius: 12px;
  border: none;
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  color: #fff;
  background: ${GRADIENT};
  transition: opacity 0.18s;

  &:hover {
    opacity: 0.9;
  }
`;

const ErrSpan = styled.span`
  color: #dc2626;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 600;
`;

const ErrorBanner = styled.div`
  padding: 14px 18px;
  border-radius: 14px;
  background: rgba(220, 38, 38, 0.08);
  border: 1px solid rgba(220, 38, 38, 0.15);
  color: #dc2626;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 600;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  padding: 32px;
  border-radius: 18px;
  background: #fff;
  border: 1px dashed rgba(23, 23, 23, 0.1);
  color: rgba(23, 23, 23, 0.45);
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  font-weight: 500;
`;

const ChartsSectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 800;
  color: #171717;
  letter-spacing: 0.02em;
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 18px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background: #fff;
  border-radius: 20px;
  padding: 22px 24px;
  box-shadow: 0 8px 24px rgba(23, 23, 23, 0.04);
  border: 1px solid rgba(23, 23, 23, 0.06);
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 12px 32px rgba(201, 77, 170, 0.08);
  }
`;

const ChartHdr = styled.div`
  margin-bottom: 18px;
  padding-bottom: 14px;
  border-bottom: 1px solid rgba(23, 23, 23, 0.06);
`;

const ChartTitle = styled.h3`
  font-family: Urbanist, sans-serif;
  font-size: 15px;
  font-weight: 800;
  color: #171717;
  margin: 0;
`;

const ChartNote = styled.p`
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  color: rgba(23, 23, 23, 0.45);
  margin: 0 0 14px;
  text-align: center;
`;

const CW = styled.div`
  width: 100%;
  height: 280px;
`;

// Outstanding payments summary card

const OutstandingGrid = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  padding: 24px 0 16px;
`;

const OutstandingItem = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const OutstandingDivider = styled.div`
  width: 1px;
  height: 56px;
  background: #e5e7eb;
  flex-shrink: 0;
`;

const OutstandingLabel = styled.div`
  font-family: "Urbanist", sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  text-align: center;
`;

const OutstandingValue = styled.div`
  font-family: "Urbanist", sans-serif;
  font-size: 28px;
  font-weight: 800;
  background: linear-gradient(144.01deg, rgba(255, 201, 81, 0.3) 0%, rgba(206, 86, 176, 0.3) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-align: center;
  line-height: 1.1;
`;

const OutstandingNote = styled.p`
  font-family: "Urbanist", sans-serif;
  font-size: 12px;
  color: #9ca3af;
  text-align: center;
  margin: 4px 0 0;
`;

// Donut legend

const PieLeg = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 14px;
  flex-wrap: wrap;
`;

const LegItem = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  font-family: "Urbanist", sans-serif;
  font-size: 13px;
  color: #4a4a4a;
`;

const LegDot = styled.span<{ $c: string }>`
  width: 9px; height: 9px; border-radius: 50%;
  background: ${(p) => p.$c};
`;

// Events list

const EventsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const EventRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 14px;
  border-radius: 14px;
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.06);
  transition: background 0.15s, border-color 0.15s;

  &:hover {
    background: #fff;
    border-color: rgba(201, 77, 170, 0.12);
  }
`;

const EventDateBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 34px;
`;

const EventDay = styled.span`
  font-family: "Urbanist", sans-serif;
  font-size: 10px;
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
`;

const EventNum = styled.span`
  font-family: "Urbanist", sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: #171717;
  line-height: 1;
`;

const EventBody = styled.div`flex: 1;`;

const EventName = styled.div`
  font-family: "Urbanist", sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #171717;
`;

const EventMeta = styled.div`
  font-family: "Urbanist", sans-serif;
  font-size: 11px;
  color: #6b7280;
  margin-top: 2px;
`;

const EventBadge = styled.span`
  font-family: "Urbanist", sans-serif;
  font-size: 10px;
  font-weight: 700;
  text-transform: capitalize;
  background: linear-gradient(144.01deg, rgba(255,201,81,0.15) 0%, rgba(201,77,170,0.15) 100%);
  color: #c94daa;
  padding: 3px 10px;
  border-radius: 999px;
`;